import { z } from 'zod';
export const MeetingSeatType = {
    Voting: 'voting',
    NonVoting: 'non_voting',
    ExOfficio: 'ex_officio',
};
/** Sentinel value indicating a seat has no term duration */
export const UNLIMITED_TERM_DURATION = -1;
/**
 * Enforces that seats with unlimited term duration cannot accept applications.
 * @param data - The seat data to transform
 * @returns The data with allowApplications forced to false when termLength is -1
 */
const enforceUnlimitedTermDuration = (data) => {
    if (data.termLength === UNLIMITED_TERM_DURATION) {
        return { ...data, allowApplications: false };
    }
    return data;
};
// Base schema without transforms (used for extending and partial derivations)
export const meetingBodySeatBaseSchema = z.object({
    number: z.number('Seat number is required'),
    title: z.string().min(1, 'Title is required'),
    requirements: z.string().nullable().optional(),
    appointedBy: z.string().nullable().optional(),
    termLength: z
        .number('Term length is required')
        .describe('Term length in months. Positive integers (1-120) set a specific term duration, which is the typical case. Use -1 to indicate an unlimited term duration.')
        .refine((val) => val === UNLIMITED_TERM_DURATION || val >= 1, {
        message: 'Term length must be greater than 0', // No duration is represented by -1
    })
        .refine((val) => val <= 120 || val === UNLIMITED_TERM_DURATION, {
        message: 'Term length must be 120 months or less',
    }),
    termLimit: z
        .number('Term limit is required')
        .describe('Use -1 to indicate no term limit. Positive integers (1-99) set a specific term limit.')
        .refine((val) => val === -1 || val >= 1, {
        message: 'Term limit must be greater than 0', // No limit is represented by -1 and is not displayed to user
    })
        .refine((val) => val <= 99, {
        message: 'Term limit must be less than 99',
    }),
    termStart: z.iso.datetime('Term start date is required'),
    type: z.enum(MeetingSeatType),
    allowApplications: z.boolean().optional(),
});
// Create validation and DTO type for both server and client (needed for different date types)
export const meetingBodySeatCreateSchema = meetingBodySeatBaseSchema.transform(enforceUnlimitedTermDuration);
export const meetingBodySeatCreateClientSchema = meetingBodySeatBaseSchema
    .extend({
    termStart: z.date('Term start date is required'),
})
    .transform(enforceUnlimitedTermDuration);
// Update validation and DTO type for both server and client (needed for different date types)
export const meetingBodySeatUpdateSchema = meetingBodySeatBaseSchema
    .partial()
    .transform(enforceUnlimitedTermDuration);
export const meetingBodySeatUpdateClientSchema = meetingBodySeatBaseSchema
    .extend({
    termStart: z.date('Term start date is required'),
})
    .partial()
    .transform(enforceUnlimitedTermDuration);
// Archive validation and DTO type (used to archive/unarchive a seat)
export const meetingBodySeatArchiveSchema = z.object({
    isArchived: z.boolean(),
});
